<template>
    <div>
        <div class="d-flex align-center flex-wrap px-3 pt-2">
            <v-btn-toggle v-model="filtro" mandatory dense borderless>
                <v-btn value="todos" small>Todos</v-btn>
                <v-btn value="abertos" small>Abertos</v-btn>
                <v-btn value="resolvidos" small>Resolvidos</v-btn>
            </v-btn-toggle>

            <span v-if="host" class="text-caption text--secondary ml-3">
                somente {{ nomeDoHost(host) }}
            </span>
            <span v-else class="text-caption text--secondary ml-3">
                todos os servidores
            </span>

            <v-spacer/>

            <v-btn icon :loading="loading" @click="carregar">
                <v-icon>mdi-refresh</v-icon>
            </v-btn>
        </div>

        <v-card elevation="2" class="ma-2">
            <v-card-text class="pa-2">
                <v-data-table
                    :headers="headers"
                    :items="itens"
                    :loading="loading"
                    :mobileBreakpoint="0"
                    :items-per-page="25"
                    :footer-props="{ itemsPerPageOptions: [25, 50, 100] }"
                    dense
                >
                    <template v-slot:no-data>
                        <div class="py-8 text-center grey--text">
                            <v-icon large color="grey">mdi-bell-off-outline</v-icon>
                            <div class="mt-2">Nenhum alerta no período.</div>
                        </div>
                    </template>

                    <template v-slot:item.severidade="{ item }">
                        <v-chip
                            x-small
                            label
                            :color="item.resolvido_em ? 'grey lighten-1' : corSeveridade(item.severidade)"
                            :dark="!item.resolvido_em"
                        >
                            {{ item.severidade === 'critico' ? 'Crítico' : 'Aviso' }}
                        </v-chip>
                    </template>

                    <template v-slot:item.regra="{ item }">
                        <v-icon
                            v-if="item.tipo === 'coleta_parada'"
                            x-small
                            class="mr-1"
                            :color="item.resolvido_em ? 'grey' : 'red'"
                        >
                            mdi-lan-disconnect
                        </v-icon>
                        <span :class="item.resolvido_em ? 'text--secondary' : 'font-weight-medium'">
                            {{ item.regra }}
                        </span>
                    </template>

                    <template v-slot:item.servidor="{ item }">
                        <span :class="item.resolvido_em ? 'text--secondary' : ''">{{ item.servidor }}</span>
                    </template>

                    <template v-slot:item.valor="{ item }">
                        {{ valorDoEvento(item, item.valor) }}
                    </template>

                    <template v-slot:item.limite="{ item }">
                        <span class="text--secondary">{{ valorDoEvento(item, item.limite) }}</span>
                    </template>

                    <template v-slot:item.disparado_em="{ item }">
                        {{ formatDate(item.disparado_em) }}
                    </template>

                    <!-- Resolvido não fica vermelho: vira registro, com a data em que voltou. -->
                    <template v-slot:item.situacao="{ item }">
                        <span v-if="item.resolvido_em" class="text--secondary">
                            <v-icon x-small color="grey">mdi-check</v-icon>
                            normalizou {{ formatDate(item.resolvido_em) }}
                        </span>
                        <span v-else :class="item.severidade === 'critico' ? 'red--text' : 'orange--text text--darken-2'">
                            <v-icon x-small :color="item.severidade === 'critico' ? 'red' : 'orange darken-2'">
                                mdi-alert-circle
                            </v-icon>
                            em aberto
                        </span>
                    </template>

                    <template v-slot:item.duracao="{ item }">
                        {{ duracao(item) }}
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import { carregarCatalogo, formatarValor } from './catalogo';
import { carregarServidores, nomeDoHost } from './servidores';

export default {
    name: 'TabAlertas',

    // null = frota inteira. É o padrão: a aba nasce mostrando todos os
    // servidores, e o filtro por servidor é o caso particular.
    props: {
        host: { type: String, default: null },
    },

    data: () => ({
        loading: false,
        filtro: 'todos',
        alertas: [],
        headers: [
            { text: 'Situação', value: 'severidade', width: 90 },
            { text: 'Regra', value: 'regra' },
            { text: 'Servidor', value: 'servidor', width: 150 },
            { text: 'Valor', value: 'valor', width: 110 },
            { text: 'Limite', value: 'limite', width: 110 },
            { text: 'Disparado em', value: 'disparado_em', width: 150 },
            { text: 'Duração', value: 'duracao', width: 100, sortable: false },
            { text: '', value: 'situacao', sortable: false, width: 220 },
        ],
    }),

    mounted() {
        this.carregar();
    },

    watch: {
        host() {
            this.carregar();
        },
    },

    computed: {
        itens() {
            if (this.filtro === 'abertos') return this.alertas.filter(a => !a.resolvido_em);
            if (this.filtro === 'resolvidos') return this.alertas.filter(a => a.resolvido_em);
            return this.alertas;
        },
    },

    methods: {
        formatarValor,
        nomeDoHost,

        valorDoEvento(item, valor) {
            if (valor === null || valor === undefined) return '—';

            if (item.tipo === 'coleta_parada') {
                return `${Math.round(valor)} min`;
            }

            return formatarValor(item.metrica, valor);
        },

        corSeveridade(severidade) {
            return severidade === 'critico' ? 'red darken-1' : 'amber darken-2';
        },

        duracao(item) {
            const fim = item.resolvido_em ? this.moment(item.resolvido_em) : this.moment();
            const minutos = fim.diff(this.moment(item.disparado_em), 'minutes');

            if (minutos < 60) return `${minutos} min`;
            if (minutos < 1440) return `${Math.floor(minutos / 60)}h ${minutos % 60}min`;
            return `${Math.floor(minutos / 1440)}d ${Math.floor((minutos % 1440) / 60)}h`;
        },

        async carregar() {
            this.loading = true;
            try {
                await carregarCatalogo(this.$http);
                await carregarServidores(this.$http);

                const { data } = await this.$http.get('monitoramento/alertas', {
                    params: this.host ? { host: this.host } : {},
                });

                this.alertas = data;
            } catch (e) {
                this.notify('Não foi possível carregar os alertas', 'error');
            }
            this.loading = false;
        },
    },
};
</script>
